import {
  Card,
  CardBody,
  FunctionCard,
  Heading,
  Paragraph,
  Separator,
  useMatchMediaTierProp,
} from "@george-labs.com/design-system"
import { DocumentForOfferDTO, GetContentRq } from "@smartx/mikro-vt-client-api"
import { saveAs } from "file-saver"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import { fileNetApi } from "../../api/apiClient"
import { useAppDispatch, useOfferData } from "../../app/hooks"
import { setBeError } from "../../app/slices/beErrorSlice"
import { setActive } from "../../app/slices/snackbarItemSlice"
import * as fileHandler from "../../functions/fileHandler"
import { useOfferScreenFlow } from "../../hooks/useOfferScreenFlow"
import { RequestHeader } from "../../utils/utils"
import {
  OfferActionButton,
  OfferNavButtons,
  OfferSaveButton,
} from "./OfferNavButtons"
import { ICON } from "@george-labs.com/george-assets/icons"

const OfferDocuments = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const offerData = useOfferData()
  const { setNextStep } = useOfferScreenFlow()

  const downloadFile = (file: DocumentForOfferDTO) => {
    dispatch(setActive(true))

    const data: GetContentRq = {
      header: new RequestHeader(),
      objectId: file.filenetId,
      caseId: offerData?.caseId,
      fileNetStorage: file.filenetStorage,
    }

    fileNetApi.getContent(data).then(
      (res) => {
        dispatch(setActive(false))
        const blobFile = fileHandler.base64toBlob(
          res.data.content,
          res.data.mimeType,
        )
        saveAs(blobFile, file.fileName)
      },
      () => {
        dispatch(setActive(false))
        setBeError(true)
      },
    )
  }

  const headerContent = (
    <Heading level={1}>{t("offers.documents.title")}</Heading>
  )

  const textContent = (
    <Paragraph style={{ marginBottom: "1rem" }}>
      {t("offers.documents.text")}
    </Paragraph>
  )

  const documentsContent = (
    <Card nested>
      {offerData?.documents?.map((file) => (
        <>
        <FunctionCard
          horizontal
          key={file.filenetId}
          icon={FunctionCard.ICON.FILE}
          heading={file.type?.name}
          buttons={[
            <OfferActionButton
              actionButtonText={t("offers.documents.button.open")}
              onActionButtonClicked={() => downloadFile(file)}
              hideIconXs
              icon={ICON.ARROW_OUT}
              key={`${file.filenetId}-button`}
            />,
          ]}
        >
          {t("offers.documents.description")}
        </FunctionCard>
        <Separator />
        </>
      ))}
    </Card>
  )

  const nextButtonHandler = () => {
    setNextStep(offerData?.caseId)
  }

  const navButtonsContent = (
    <OfferNavButtons onNextButtonClicked={nextButtonHandler} />
  )

  const saveButtonHandler = () => {
    navigate("/dashboard", { replace: true })
  }

  const saveButtonContent = (
    <OfferSaveButton onSaveButtonClicked={saveButtonHandler} />
  )

  const pageContent = useMatchMediaTierProp({
    xs: (
      <>
        {headerContent}

        {textContent}

        {documentsContent}

        {navButtonsContent}
      </>
    ),
    md: (
      <Card nested={false}>
        <CardBody>
          {headerContent}

          {textContent}

          {documentsContent}
        </CardBody>

        <Separator />

        <CardBody>{navButtonsContent}</CardBody>
      </Card>
    ),
  })

  return (
    <>
      {pageContent}

      {saveButtonContent}
    </>
  )
}

export default OfferDocuments
